import { Component, OnInit, ViewChild, ElementRef, DoCheck, OnDestroy, HostListener, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CampaignName, HelperService } from '../shared/services/helper.service';
import { AuthService } from '../shared/services/auth.service';
import { EmitterService } from '../shared/services/emitter.service';
import { PushNotificationComponent } from '../shared/components/push-notification/push-notification.component';
import { NotificationService } from '../shared/services/notification.service';
import { BroadcastService } from '../shared/services/broadcast.service';
import { JwtHelperService } from '@auth0/angular-jwt';

import { DateHelperService } from '../shared/services/date-helper.service';
import { UserService } from '../shared/services/user.service';
import { OverlayService } from '../shared/services/overlayService';
import { ProjectService } from '../shared/services/project.service';
import {
  listenerWSConnectionChange,
  WS_STATUS_CONNECTING,
  WS_STATUS_CONNECTED,
  WS_STATUS_CLOSED,
  WS_SEND_DESTINATION_TYPES,
  wsListenerCONVERSATION,
  wsListenerAUTH_USER_POPUP_NOTIFY,
  wsListenerWORKSPACE_DELETE,
  MtmStompService,
} from '../shared/services/mtm-stomp.service';
import { TranslateService } from '../shared/services/translate.service';
import { takeUntil } from 'rxjs/operators';
import { Subject, timer } from 'rxjs';
import { WorkspaceService } from '../shared/services/workspace.service';
import { AdminService } from '../admin/services/admin.service';
import { ThemeService } from '../shared/services/theme.service';
import {
  ActiveWorkspaceUpdatedEvent,
  ComapanyCoverUpdatedEvent,
  FeatureTypes,
  HeaderInfo,
  ProfileSettings,
  ThemeOptions,
  WorkspaceCoverUpdatedEvent,
  WorkspaceDeleted,
  WorkspaceDTO,
} from '../shared/interfaces';
import { TranslatePipe } from '../shared/pipes/translate.pipe';
import { FileTransferService, listenerFileDownloadCompleted } from '../shared/services/signed-url';
import { BannerTypes } from '../shared/components/info-banner/info-banner.component';
import { SecurityPolicyService } from '../shared/services/security-policy.service';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { SharedIdleExpiry } from '../shared/services/shared-idle-expiry';
import { WarningInactiveModalComponent } from '../shared/components/warning-inactive-modal/warning-inactive-modal.component';
import { WarningLoggedoutModalComponent } from '../shared/components/warning-loggedout-modal/warning-loggedout-modal.component';
import { ComponentStateService } from '../shared/services/component-state.service';
import { MtmBreadcrumbLink, MtmBreadcrumbService } from '../shared/services/mtm-breadcrumb.service';
import { CommonLogMessages, LogService } from '../shared/services/log.service';
import { FeatureConfigService } from '../shared/services/feature-config.service';
import { GoogleAnalyticsService } from '../shared/services/google-analytics.service';
import { GeneralUtilsService } from '../shared/services/general-utils.service';
import { currentAppToken } from '../shared/auth/app-token';

@Component({
  selector: 'mtm-layout',
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  standalone: false,
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  @ViewChild('mainLayoutContainer', { static: false }) mainLayoutContainer: ElementRef;
  @ViewChild(PushNotificationComponent) pushNotification: PushNotificationComponent;

  loading: boolean = false;
  rightbarCollapsed: boolean = false;
  modalRef: NgbModalRef = null;
  isModalOpen: boolean = false;
  isVideoCallModalOpen: boolean = false;
  project: any = null;
  projectId: any = null;
  duration: number = 0;
  proposal: any[] = [];
  userProfile: ProfileSettings | null = null;
  private intervalWSProjectActiveUser: any;

  hasDebt: boolean = false;
  private backendStatus = null;
  private intervalVerfyToken: any;
  publicReviewLink: boolean = false;
  publicPage: boolean = false;
  ngUnsubscribe = new Subject();
  authUser: any;
  // Signal-backed: pages publish these from ngAfterViewInit, after this view was checked. The app is
  // not zone-driven, so a plain field written then would not render until an unrelated global tick.
  private readonly themeOptionsSignal = signal<ThemeOptions>({
    rightPane: null,
    backgroundImage: null,
    header: null,
    fullWidthContent: false,
    hasHeaderInfo: true,
  });
  get themeOptions(): ThemeOptions {
    return this.themeOptionsSignal();
  }
  private readonly headerInfoSignal = signal<HeaderInfo>({
    title: '',
    subtitle: '',
    avatarName: '',
    avatarImage: '',
    subtitleParams: {},
  });
  get headerInfo(): HeaderInfo {
    return this.headerInfoSignal();
  }
  workspaceCover: string = '';
  workspaceId: any;
  workspaceName: any;
  wsId: string;
  companyCover: string = '';
  bannerTypes = BannerTypes;

  lastAccessedWorkspace: string;
  logoutTimeout: any;
  logoutTime: any;
  isNavExpanded: boolean = false;

  idleState = 'Not started.'; // Not used at the moment, but it's a good idea to keep it here for future use
  timedOut = false;
  // lastPing?: Date = null;
  DEFAULT_AUTOLOGOUT_TIME = 10; // In minutes, default value in case logoutTime is not defined
  DEFAULT_AUTOLOGOUT_AFTER_IDLE = 5; // In minutes
  /**
   * Auto-logout delay (minutes) last read from the company security policy, shared by every layout
   * instance; 0 means auto-logout is disabled. Most top-level routes render their own MainLayoutComponent,
   * so each navigation between them destroys one instance (which stops the shared Idle) and creates
   * another: the new one restarts the watcher with this value right away instead of waiting for — and
   * depending on — its own security-policy call, which used to leave the watcher stopped when it failed.
   */
  private static lastAutoLogoutMinutes: number | null = null;
  private idleSubscribed = false;
  LOGOUT_INACTIVITY_KEY = 'loggedOutDueToInactivityMessage';
  warningModalRef: any;
  showNewBreadcrumb: boolean = false;
  activeModalRefs: NgbModalRef[] = [];
  private readonly MOBILE_BREAKPOINT = 768; // Standard mobile breakpoint
  private resizeObserver: ResizeObserver;

  // Feature config variables
  campaignName: string;

  constructor(
    private readonly modalService: NgbModal,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly notificationService: NotificationService,
    private readonly broadcastService: BroadcastService,
    public readonly userService: UserService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly overlayService: OverlayService,
    private readonly projectService: ProjectService,
    private readonly translateService: TranslateService,
    private readonly workspaceService: WorkspaceService,
    private readonly adminService: AdminService,
    private readonly themeService: ThemeService,
    private readonly elementRef: ElementRef,
    public readonly translatePipe: TranslatePipe,
    private readonly stompService: MtmStompService,
    private readonly securityPolicyService: SecurityPolicyService,
    private readonly idle: Idle,
    private readonly idleExpiry: SharedIdleExpiry,
    private readonly componentStateService: ComponentStateService,
    private readonly mtmBreadcrumbService: MtmBreadcrumbService,
    private readonly logService: LogService,
    private readonly transferService: FileTransferService,
    private readonly generalUtilsService: GeneralUtilsService,
    private readonly helperService: HelperService,
    private readonly dateHelperService: DateHelperService,
    private readonly featureConfigService: FeatureConfigService
  ) {
    document.body.className += ' less-space';
    this.wsId = this.stompService.subscribeToService();
    this.mtmBreadcrumbService.$linkSubject.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (links: MtmBreadcrumbLink[]) => {
        this.showNewBreadcrumb = links.length > 0;
      },
      error: (error) => {
        console.log(error);
      },
    });
    EmitterService.get('user.logout')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (!this.activeModalRefs) {
          return;
        }
        this.activeModalRefs.forEach((modal) => {
          try {
            modal.dismiss();
          } catch (ex) {
            console.error('closing modal error', ex);
          }
        });

        this.projectService.leaveFromProject();
        this.workspaceService.unsetActiveWorkspace();
        this.workspaceService.clearWorkspaceCache();
        this.stompService.disconnectWS();
      });

    EmitterService.get('SECURITY_POLICY:AUTO_LOGOUT')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((time) => {
        // if (!this.authUser) {
        //   return;
        // }
        // this.logoutTime = time;
        // this.idle.setIdle(this.logoutTime * 60);
        // this.reset();
      });

    this.publicReviewLink = this.route.snapshot.data.publicReviewLink;
    this.publicPage = this.route.snapshot.data.publicPage;
    this.workspaceService
      .getCurrentWorkspace()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: WorkspaceDTO | null) => {
        this.workspaceId = result?.id || '';
        this.workspaceName = result?.name || '';
      });

    this.scheduleNextTokenVerification();
    this.stompService.initConnectWS();

    listenerWSConnectionChange
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((status) => this.changesConnection(status));

    this.buildWSProjectActiveUserInterval();
    this.authUser = this.authService.getAuthUser();
    if (this.authUser) {
      this.userService.getUserProfileSettings().subscribe((profile: any) => {
        this.userProfile = profile;
      });
      this.stompService.subscribeToListener(
        'CONVERSATION',
        this.wsId,
        [this.authUser.username],
        (args: any) => this.processMessage(args),
        wsListenerCONVERSATION
      );
      this.stompService.subscribeToListener(
        'USER_POPUP_NOTIFY',
        this.wsId,
        [this.authUser.username],
        (args: any) => this.popupNotificationAdded(args),
        wsListenerAUTH_USER_POPUP_NOTIFY
      );
      this.stompService.subscribeToListener(
        'WORKSPACE_DELETE',
        this.wsId,
        [this.authUser.username],
        (args: any) => this.workspaceDeleted(args),
        wsListenerWORKSPACE_DELETE
      );
    }

    listenerFileDownloadCompleted.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (result) => {
        const downloadControl = document.getElementById(result.id);
        if (downloadControl) {
          downloadControl.classList.remove('downloading');
        }
      },
      error: () => {},
    });
  }

  get headerBgImage(): string {
    return this.themeOptions?.backgroundImage ?? '';
  }

  get isNgBreadcrumbVisible() {
    return this.projectService.projectOnloaded;
  }

  workspaceDeleted(args) {
    EmitterService.get(WorkspaceDeleted).emit(args);
  }

  getSelectedLanguage(lang: any) {
    const selectedLanguage = localStorage.getItem('auth_user_language') || 'en_us';
    return lang === selectedLanguage;
  }

  popupNotificationAdded(message) {
    EmitterService.get('popup.notification').emit(message);
  }

  private changesConnection(status) {
    this.backendStatus = status;

    switch (this.backendStatus) {
      case WS_STATUS_CLOSED:
        this.googleAnalyticsService.sendEvent(
          'Recover',
          'Backend.down',
          this.authService.getAuthUser() ? this.authService.getAuthUserName() : ''
        );
        break;
      case WS_STATUS_CONNECTING:
        break;
      case WS_STATUS_CONNECTED:
        this.googleAnalyticsService.sendEvent(
          'Recover',
          'Backend.up',
          this.authService.getAuthUser() ? this.authService.getAuthUserName() : ''
        );
        break;
    }
  }

  private buildVerifyTokenInterval() {
    // this.breakVerifyTokenInterval();
    // this.intervalVerfyToken = setInterval(() => this.verifyAuthToken(), 30000);
  }

  private breakVerifyTokenInterval() {
    if (this.intervalVerfyToken) clearInterval(this.intervalVerfyToken);
  }

  private scheduleNextTokenVerification() {
    const token = currentAppToken();
    if (this.backendStatus != WS_STATUS_CONNECTED && !token) {
      return;
    }

    const jwtHelper: JwtHelperService = new JwtHelperService();
    const nextTokenExpiry = jwtHelper.getTokenExpirationDate(token);
    const timeUntilNextCheck = nextTokenExpiry.getTime() - Date.now() - 10 * 60 * 1000; // 10 minutes before expiry
    timer(timeUntilNextCheck)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.buildVerifyTokenInterval();
      });
  }

  private verifyAuthToken() {
    if (this.backendStatus != WS_STATUS_CONNECTED && !currentAppToken()) return;
    // While impersonating ("switch user"), `localStorage['app_token']` is a gateway switch-user token.
    // `/api/users/token/renew` can re-derive identity and return the admin's token, which would
    // overwrite the switch-user session. Skip auto-renewal — the impersonation session simply ends
    // at the switch-user token's natural expiry.
    if (this.authService.isImpersonating()) return;

    const jwtHelper: JwtHelperService = new JwtHelperService();
    const token = currentAppToken();

    // Check if token is going to expire
    const now = new Date();

    // Check if token is going to expire in next 5 mins
    // If that is the case, then request for new token
    // If error trying to get new token - display error message and redirect to Login page
    const tokenExpiry = jwtHelper.getTokenExpirationDate(token);
    if (token && this.dateHelperService.addMinutesToDate(now, 5) > tokenExpiry) {
      this.userService
        .getToken()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (data: any) => {
            console.log('renewing token');
            this.authService.changeToken(data.token);
            this.breakVerifyTokenInterval();

            // Schedule next token verification 10 minutes before expiry using rxjs timer
            this.scheduleNextTokenVerification();

            // GA: Event token is expired
            this.googleAnalyticsService.sendEvent(
              'Recover',
              'Token.expired',
              this.authService.getAuthUser() ? this.authService.getAuthUserName() : ''
            );
          },
          error: (err: any) => {
            if (err?.status === 401) {
              const extraInfo: any = {
                tokenExpiry,
                token,
                cause: 'token renewal failed',
                error: err,
                username: this.authUser?.username,
              };
              this.logService.logMessage('warning', CommonLogMessages.AutoLogout, extraInfo);
              this.authService.logout({ message: 'token expired' });
              window.location.href = '/entry';
            }
          },
        });
    }
  }

  @HostListener('window:storage', ['$event'])
  public onStorage(event: StorageEvent): void {
    // Listen for localStorage changes and fire message_receive method
    this.broadcastService.receive(event);
  }

  public ngOnInit(): void {
    this.checkWorkspaceCover();
    this.setCompanySecurityConfig();

    this.componentStateService.setMainLayoutComponent(this);

    // this.subscribeToClearStorage();

    EmitterService.get(ActiveWorkspaceUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.checkWorkspaceCover();
      });

    EmitterService.get(WorkspaceCoverUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((cover) => {
        if (cover) {
          this.workspaceCover = `url(${this.generalUtilsService.getCacheBusterUrl(cover)})`;
        } else {
          this.workspaceCover = '';
        }
      });

    EmitterService.get(ComapanyCoverUpdatedEvent)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((cover) => {
        if (cover) {
          this.companyCover = `url(${this.generalUtilsService.getCacheBusterUrl(cover)})`;
        } else {
          this.companyCover = '';
        }
      });

    // Spread: addMainContainerClasses re-emits the same mutated object, which a signal set would ignore.
    this.themeService.contentParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((update) => this.themeOptionsSignal.set({ ...update }));

    this.themeService.headerParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((headerInfo) => this.headerInfoSignal.set(headerInfo));

    this.modalService.activeInstances.pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (modalRefs: NgbModalRef[]) => {
        this.activeModalRefs = modalRefs;
      },
    });

    EmitterService.get('NAV:EXPANDED')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (params) => {
          this.isNavExpanded = params?.expanded;
        },
      });

    this.checkScreenSize();
    this.setupResizeObserver();

    this.featureConfigService
      .getFeatureConfigs([FeatureTypes.FEA_CAMPAIGN_MAIN])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          const campaignMainConfig = response[FeatureTypes.FEA_CAMPAIGN_MAIN];

          // Translate campaign name from BE to localization key
          const featureConfigCampaignName = this.helperService.translateConfigCampaignNameToLocalizationKey(
            campaignMainConfig?.campaignName as CampaignName,
            'campaignName'
          );
          if (featureConfigCampaignName) this.campaignName = featureConfigCampaignName;
        },
      });
  }

  ngOnDestroy() {
    this.stompService.unsubscribeToService(this.wsId);
    this.breakWSProjectActiveUserInterval();
    this.breakVerifyTokenInterval();
    this.clearIdleWatcher();
    this.idle.ngOnDestroy();
    this.closeWarningModal();
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Clears the idle watcher state.
   */
  clearIdleWatcher() {
    this.idle.stop();
  }

  setCompanySecurityConfig() {
    const { companyId } = this.authUser || {};
    if (!companyId) {
      return;
    }
    if (MainLayoutComponent.lastAutoLogoutMinutes !== null) {
      this.startIdleWatcher(MainLayoutComponent.lastAutoLogoutMinutes);
    }
    this.securityPolicyService
      .getSecurityConfig(companyId)
      // A late response must not reach a destroyed instance (it would subscribe zombie idle handlers).
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          const autoLogoutConfig = res?.find((c) => c.configKey === 'AUTO_LOGOUT');
          const minutes =
            autoLogoutConfig?.configValue === '0'
              ? 0
              : parseInt(autoLogoutConfig?.configValue || this.DEFAULT_AUTOLOGOUT_TIME.toString());
          MainLayoutComponent.lastAutoLogoutMinutes = minutes;
          this.startIdleWatcher(minutes);
        },
        error: () => {
          // Keep enforcing auto-logout: the last known delay already runs; otherwise fall back to the default.
          if (MainLayoutComponent.lastAutoLogoutMinutes === null) {
            this.startIdleWatcher(this.DEFAULT_AUTOLOGOUT_TIME);
          }
        },
      });
  }

  /**
   * Applies the auto-logout delay and (re)starts the shared idle watcher. Idle events are subscribed once
   * per instance — subscribing again on every policy refresh stacked duplicate warning modals and logouts.
   * @param minutes - Idle delay before the inactivity warning; 0 disables auto-logout.
   */
  private startIdleWatcher(minutes: number): void {
    if (!this.authUser) {
      return;
    }
    if (minutes <= 0) {
      this.clearIdleWatcher();
      return;
    }
    console.log('set idle timeout', minutes);
    this.logoutTime = minutes;
    this.setIdleTimeout(minutes);
    if (this.idleSubscribed) {
      this.reset();
      return;
    }
    this.idleSubscribed = true;
    this.subscribeToIdle();
  }

  /**
   * Sets the idle timeout configuration.
   *
   * This function configures the idle service with the specified idle time,
   * the default auto-logout time after idle, and the default interrupt sources (mouse, keyboard, etc events).
   *
   * @param idleTime - The idle time in minutes before the user is considered idle.
   */
  setIdleTimeout(idleTime: number) {
    this.idle.setIdle(idleTime * 60);
    this.idle.setTimeout(this.DEFAULT_AUTOLOGOUT_AFTER_IDLE * 60);
    this.idleExpiry.setWindow(idleTime * 60 + this.DEFAULT_AUTOLOGOUT_AFTER_IDLE * 60);
    // setInterrupts() detaches the current sources, and Idle.watch() only re-attaches them when the
    // watcher is stopped -- re-applying the delay to a running watcher (cached delay, then the policy
    // response, as after a re-login without reload) would leave it deaf to activity until it logs out.
    if (!this.idle.getInterrupts().length) {
      this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    }
  }

  /**
   * Subscribes to idle events and handles the idle state transitions.
   * This function sets up listeners for various idle events such as idle start, idle end, timeout, and timeout warning.
   * It also manages the display of a warning modal when the user goes idle and handles the logout process when the user times out.
   */
  subscribeToIdle() {
    /**
     * Handles the event when the user is no longer idle.
     * Resets the idle watcher.
     */
    this.idle.onIdleEnd.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      console.log('no longer idle');
      this.idleState = 'No longer idle.';
      this.reset();
      this.closeWarningModal();
    });

    /**
     * Handles the event when the user times out.
     * Closes the warning modal if open and logs the user out.
     */
    this.idle.onTimeout.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      console.log('timed out');
      this.idleState = 'Timed out!';
      this.timedOut = true;
      this.closeWarningModal();
      this.logout(true);
    });

    /**
     * Handles the event when the user goes idle.
     * Displays a warning modal to the user.
     */
    this.idle.onIdleStart.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.idleState = "You've gone idle!";
      console.log('gone idle');
      this.showWarningModal(); // OAFIXME - show warning modal when user goes idle
    });

    /**
     * Handles the event when the user is about to time out.
     * Updates the countdown in the warning modal.
     * @param countdown - The number of seconds remaining before timeout.
     */
    this.idle.onTimeoutWarning.pipe(takeUntil(this.ngUnsubscribe)).subscribe((countdown) => {
      if (this.transferService.hasActiveTransfers()) {
        console.log('reset timeout because of active transfers');
        this.reset();
        return;
      }

      this.idleState = 'You will time out in ' + countdown + ' seconds!';
      if (this.warningModalRef) {
        this.warningModalRef.componentInstance.countdown = countdown;
      }
    });
    this.reset();
  }

  /**
   * Opens the warning modal to alert the user about impending auto-logout.
   * Sets the initial countdown time in the modal.
   */
  showWarningModal() {
    // Open the warning modal component
    this.warningModalRef = this.modalService.open(WarningInactiveModalComponent);
    // Set the initial countdown time in the modal instance
    this.warningModalRef.componentInstance.countdown = this.DEFAULT_AUTOLOGOUT_AFTER_IDLE;
  }

  /**
   * Closes the warning modal if it is currently open.
   */
  closeWarningModal() {
    // Check if the warning modal exists to avoid errors when closing
    if (this.warningModalRef) {
      this.warningModalRef.close();
    }
  }

  /**
   * Resets the idle watcher
   */
  reset() {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  /**
   * Logs out the user and optionally shows the inactivity modal.
   * @param showInactivityLogoutModal - Whether to show the logged out due to inactivity or not. Defaults to false.
   */
  logout(showInactivityLogoutModal: boolean = false) {
    this.clearIdleWatcher();
    this.logService.logMessage('warning', CommonLogMessages.AutoLogout, {
      cause: 'inactivity',
      username: this.authUser?.username,
    });
    this.authService.logout({ message: this.LOGOUT_INACTIVITY_KEY });
    EmitterService.get('user.logout').emit();
    this.broadcastService.send({ action: 'user.logout' });
    this.router.navigate(['/entry']).then(() => {
      if (showInactivityLogoutModal) {
        const modalRef = this.modalService.open(WarningLoggedoutModalComponent);
        modalRef.componentInstance.logoutMessageKey = this.LOGOUT_INACTIVITY_KEY; // Translations key for inactivity logout message
      }
    });
  }

  resetTimer() {
    clearTimeout(this.logoutTimeout);
    if (this.logoutTime) {
      this.logoutTimeout = setTimeout(this.logout.bind(this), this.logoutTime * 60000);
    }
  }

  setAutoLogout() {
    document.addEventListener('mousedown', this.resetTimer.bind(this), true);
    document.addEventListener('mousemove', this.resetTimer.bind(this), true);
    document.addEventListener('keypress', this.resetTimer.bind(this), true);
    document.addEventListener('scroll', this.resetTimer.bind(this), true);
    document.addEventListener('touchstart', this.resetTimer.bind(this), true);
  }

  /**
   * Process message from web socket
   * @param message
   */
  processMessage(message: any) {
    if (!message.object) message.object = JSON.parse(message.objectJson);
    if (message.code === 'CHANGED_COMPANY_ROLE' && message.username === this.authService.getAuthUserName()) {
      this.notificationService
        .open({
          title: this.translatePipe.transform('roleChanged_title'),
          description: this.translatePipe.transform('roleChanged_relogin'),
          confirmBtn: this.translatePipe.transform('accept'),
        })
        .subscribe(() => {
          this.logService.logMessage('warning', CommonLogMessages.AutoLogout, {
            cause: 'roleChanged',
            username: this.authUser?.username,
            ...message,
          });
          this.authService.logout({ message: 'role changed' });
          window.location.href = '/entry';
        });
      return;
    }
  }

  /**
   * Redirect to home page
   */
  homePage() {
    if (this.publicReviewLink) {
      return;
    }
    let redirectUrl = '/projects';
    if (this.project?.id) redirectUrl = redirectUrl + `/${this.projectId}/dashboard`;

    this.router.navigate([redirectUrl]);
  }

  changeLang(lang: any) {
    this.translateService.use(lang);
    const registerData = JSON.parse(localStorage.getItem('register_data'));
    this.authService.setAuthLanguage(lang);
    if (registerData) {
      return;
    }
    this.userService
      .changeUserProfileSettingLang(lang)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((userProfileSettings) => {});
    this.notificationService.getAllUnseenNotifications();
  }

  private breakWSProjectActiveUserInterval() {
    if (!this.intervalWSProjectActiveUser) return;

    clearInterval(this.intervalWSProjectActiveUser);
  }

  private buildWSProjectActiveUserInterval() {
    this.breakWSProjectActiveUserInterval();
    this.sendUserActiveInProject();
    this.intervalWSProjectActiveUser = setInterval(() => this.sendUserActiveInProject(), 1000);
  }

  private sendUserActiveInProject() {
    this.stompService.sendToWS(WS_SEND_DESTINATION_TYPES.ONLINE_USER, [], undefined, {
      user: this.authService.getAuthUserName(),
    });
    if (this.projectId) {
      this.stompService.sendToWS(WS_SEND_DESTINATION_TYPES.ACTIVE_PROJECT, [this.projectId]);
    }
  }

  private checkWorkspaceCover() {
    const activeWorkspace = this.workspaceService.getActiveWorkspace();
    if (!activeWorkspace) {
      this.workspaceCover = '';
      return;
    }

    this.workspaceService
      .getWorkspaceById(activeWorkspace.companyId, activeWorkspace.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((workspace: WorkspaceDTO | null) => {
        if (workspace) {
          this.workspaceService.setActiveWorkspaceCoverAndAvatar({
            avatar: workspace.avatarUrl,
            cover: workspace.coverUrl,
          });
          if (workspace.coverUrl) {
            const cover = this.generalUtilsService.getCacheBusterUrl(workspace.coverUrl);
            this.workspaceCover = `url(${cover})`;
          } else {
            this.workspaceCover = '';
          }
        }
      });
  }

  backToAdmin() {
    this.adminService.switchToAdmin();
  }

  onBehalf() {
    return this.authService.isImpersonating();
  }

  /**
   * Checks if the current screen size is mobile
   * @returns boolean indicating if screen is mobile size
   */
  private isMobileScreen(): boolean {
    return window.innerWidth <= this.MOBILE_BREAKPOINT;
  }

  /**
   * Checks screen size and emits mobile view toggle event if needed
   */
  private checkScreenSize(): void {
    const isMobileView = this.isMobileScreen();
    EmitterService.get('TOGGLE:MOBILE_VIEW').emit({ isMobileView });
  }

  /**
   * Sets up ResizeObserver to watch for window size changes
   */
  private setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.checkScreenSize();
    });

    // Observe the document body for size changes
    this.resizeObserver.observe(document.body);
  }
}
